<template>
  <v-select 
    autocomplete="ftsSelect"
    :append-to-body="appendToBody"
    :calculate-position="withPopper"
    :label="label"
    :loading="loading"
    :multiple="multiple"
    :options="options"
    :reduce="getReduce"
    v-model="model"
    :placeholder="placeholder"
    :filterable="filterable"
    :getOptionKey="getOptionKey"
    :selectable="selectable"
    :clearable="clearable"
    :clearSearchOnBlur="clearSearchOnBlur"
    :clearSearchOnSelect="clearSearchOnSelect"
    :closeOnSelect="closeOnSelect"
    class="fts-select"
    :class="{'comfort-design': comfortDesign}"
    @open="onOpen"
    @close="onClose"
    @search="onSearch"
    @search:focus="onSearchFocus"
    @search:blur="onSearchBlur"
    @option:selected="optionSelected"
    ref="ftsSelect"
  >
    <!-- selected option -->
    <template #selected-option-container="{ option, deselect, multiple, disabled }">
      <div class="selected-option" :class="{ 'multiple': multiple }">  
        <slot name="selected-option-container" :option="option">
          <div>{{option[label]}}</div>
        </slot>
        <font-awesome-icon
          :icon="['fas', 'times']"
          :disabled="disabled"
          class="deselect-icon"
          @mousedown.stop="deselectOption(deselect, option)"
          v-if="multiple"
        />
      </div>
    </template>
    <!-- search -->
    <template #search="{ attributes, events }">
      <input class="vs__search fts-select-search" v-bind="attributes" v-on="events">
    </template>
    <!-- open indicator -->
    <template #open-indicator="{ attributes }">
      <slot name="open-indicator" :attributes="attributes" />
    </template>
    <!-- list header -->
    <template #list-header="{ search, loading, searching, filteredOptions }">
      <slot
        name="list-header"
        :search="search"
        :loading="loading"
        :searching="searching"
        :filteredOptions="filteredOptions"
        v-if="filteredOptions && filteredOptions.length"
      />
    </template>
    <!-- list footer -->
    <template #list-footer="{ search, loading, searching, filteredOptions }">
      <slot
        name="list-footer"
        :search="search"
        :loading="loading"
        :searching="searching"
        :filteredOptions="filteredOptions"
        v-if="filteredOptions && filteredOptions.length"
      />
    </template>
    <!-- option -->
    <template #option="option">
      <slot name="option" :option="option" />
    </template>
    <!-- no options -->
    <template #no-options="{ search, searching, loading }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
        :loading="loading"
      >
        <div class="search-no-options">
          <!-- no match option -->
          <div class="search-no-options-wrapper " v-if="search">
            <font-awesome-icon
              :icon="['fas', 'search']"
              class="search-no-options-icon"
            />
            <div>
              {{noMatchOptionsText}}
            </div>
          </div>
          <!-- no option -->
          <div class="search-no-options-wrapper" v-else>
            <font-awesome-icon
              :icon="['fas', 'exclamation-circle']"
              class="search-no-options-icon"
            />
            <div>
              {{noOptionsText}}
            </div>
          </div>
          <!-- footer -->
          <slot
            name="no-options-footer"
            :search="search"
            :searching="searching"
            :loading="loading"
          />
        </div>
      </slot>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select';
import "vue-select/dist/vue-select.css";
import { createPopper } from '@popperjs/core';

export default {
  props: {
    appendToBody: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: "name"
    },
    reduce: Function,
    reduceKey: {
      type: String,
      default: "id"
    },
    multiple: Boolean,
    formData: {
      type: Object
    },
    item: {
      type: Object
    },
    value: {
      type: [Object, String, Array],
    },
    options: {
      type: Array
    },
    placeholder: {
      type: String,
      default: "Please select"
    },
    noMatchOptionsText: {
      type: String,
      default: "No matching options"
    },
    noOptionsText: {
      type: String,
      default: "No options found"
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    getOptionKey: Function,
    selectable: Function,
    clearable: {
      type: Boolean,
      default: true
    },
    clearSearchOnBlur: Function,
    clearSearchOnSelect: {
      type: Boolean,
      default: true,
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    popperPlacement: {
      type: String,
      default: 'bottom-start'
    },
    popperWidth: String,
    popperOffset: {
      type: Array,
      default: () => [0, 3]
    },
    comfortDesign: Boolean,
    /**
     * current only work with appendToBody
     */
    appendDropdownClass: String,
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  components: {
    "v-select": vSelect
  },
  data: () => ({
    loading: false,
  }),
  methods: {
    withPopper(dropdownList, component, {width}) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = this.popperWidth ? this.popperWidth : width;
      dropdownList.style.maxWidth = "100%"
      dropdownList.classList.add("fts-scrollbar");
      if (this.appendDropdownClass) dropdownList.classList.add(this.appendDropdownClass);
      if (this.comfortDesign) dropdownList.classList.add("comfort-design");
      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.popperPlacement,
        modifiers: [
          {
            name: 'offset', options: {
              offset: this.popperOffset
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          }]
      });
      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => popper.destroy();
    },
    onLoading(arg) {
      this.loading = arg;
    },
    onOpen() {
      this.$emit("open", this.onLoading);
    },
    onClose() {
      this.$emit("close");
    },
    onSearch(searchText, loading) {
      this.$emit('search', {
        searchText: searchText,
        loading: loading
      });
    },
    onSearchFocus() {
      this.$emit('search:focus', this.onLoading);
    },
    onSearchBlur() {
      this.$emit('search:blur');
    },
    optionSelected(val) {
      this.$emit('optionSelected', val);
      this.$emit('option:selected', val);
    },
    deselectOption(deselect, option) {
      /**
       * denormalize option (deselect(option without normalize))
       * Select.vue normalizeOptionForSlot(option)
       */
      const findOption = this.model.find(x =>
        typeof x !== 'object' && x === option[this.label]
      );
      deselect(findOption ? findOption : option);
    },
  },
  computed: {
    model: {
      get() {
        const data = this.item && this.item.fieldName ? this.formData[this.item.fieldName] : this.value;
        if (this.multiple && Array.isArray(data)) {
          return data.filter(x => x !== null && x !== undefined);
        }
        return data;
      },
      set(val) {
        if (val !== null && val !== undefined) {
          if (this.item && this.item.fieldName)
            this.$set(this.formData, this.item.fieldName, val);
          else
            this.$emit('updateValue', val);
        } else {
          if (this.item && this.item.fieldName)
            this.$delete(this.formData, this.item.fieldName)
          else
            this.$emit('updateValue', val);
          
          this.$emit('clearSelection');
        }

        this.$emit('input', val);
      }
    },
    getReduce() {
      return this.reduce ? 
        this.reduce : 
        x => Object.prototype.toString.call(x) === '[object Object]' ?
          x[this.reduceKey] : x
      ;
    },
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-select {
  &:not(.custom) {
    &.vs--single {
      &.vs--open {
        .selected-option {
          @include absolute-center-y;
          left: .75rem;
          right: .75rem;
          opacity: .4;
          margin: auto;
        }
      }
      &.vs--searching {
        .selected-option {
          display: none;
        }
      }
      .vs__selected-options {
        flex-wrap: nowrap;
        .selected-option {
          > div {
            @include text-overflow-ellipsis;
          }
        }
      }
    }
    &.comfort-design {
      $dropdownToggleFontSize: $comfortDesignFontSize;
      .vs__dropdown-toggle {
        min-height: $comfortDesignHeight;
        border-radius: $comfortDesignBorderRadius;
        font-size: $dropdownToggleFontSize;
        .vs__selected-options {
          .selected-option {
            &.multiple {
              border-radius: $comfortDesignBorderRadius;
            }
          }
          .vs__search {
            @include placeholder($comfortDesignPlaceholderFontSize);
            font-size: $dropdownToggleFontSize;
            line-height: 1.2;
          }
        }
      }
    }
    &:not(.comfort-design) {
      $dropdownToggleFontSize: .75rem;
      .vs__dropdown-toggle {
        min-height: $height;
        border-radius: 0;
        font-size: $dropdownToggleFontSize;
        .vs__selected-options {
          .vs__search {
            font-size: $dropdownToggleFontSize;
          }
        }
      }
    }
    // &.vs--open {
    //   .vs__search {
    //     padding-left: .75rem;
    //     padding-right: .75rem;
    //   }
    // }
    .vs__dropdown-toggle { 
      min-width: 0;
      padding: 0;
      border: 1px solid var(--fts-form-control-border);
      background: var(--fts-form-control-background);
      color: var(--fts-form-control);
      &:focus-within {
        border-color: $primaryColor;
        outline: none;
        -webkit-box-shadow: $inputBoxShadow;
        box-shadow: $inputBoxShadow;
      }
    }
    .vs__selected-options {
      min-width: 0;
      padding: 0 .375rem;
      // Selected Option
      .selected-option {
        min-width: 0;
        display: flex;
        align-items: center;
        margin: .175rem 0 .175rem .375rem;
        &.multiple {
          padding: .275rem;
          background: var(--fts-gray-2);
          .deselect-icon {
            margin-left: .75rem;
            margin-right: .375rem;
            color: var(--fts-gray-5);
            font-size: .75rem;
            &:hover {
              color: var(--fts-danger);
              cursor: pointer;
            }
          }
        }
      } 
      // Input
      .vs__search {
        margin: 0;
        padding: 0 .375rem;
      }
    }
    .vs__actions {
      padding: 0 .75rem 0 .375rem;
      .vs__clear {
        margin-right: .375rem;
        fill: var(--fts-gray-5);
      }
      .vs__open-indicator {
        fill: var(--fts-gray-5);
      }
      .select-open-indicator {
        color: var(--fts-gray-5);
        font-size: 1rem;
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
  .vs__dropdown-toggle {
    .vs__search {
      color: var(--fts-text);
      &::placeholder {
        color: var(--fts-form-control-placeholder);
      }
    }
  }
}

// .v-select.drop-up.vs--open .vs__dropdown-toggle {
//   border-radius: 0 0 4px 4px;
//   border-top-color: transparent;
//   border-bottom-color: rgba(60, 60, 60, 0.26);
// }

// [data-popper-placement='top'] {
//   border-radius: 4px 4px 0 0;
//   border-top-style: solid;
//   border-bottom-style: none;
//   box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15)
// }

// Override
.vs__dropdown-menu {
  &.comfort-design {
    $dropdownOptionFontSize: $comfortDesignFontSize;
    .vs__dropdown-option {
      padding-top: .375rem;
      padding-bottom: .375rem;
      font-size: $dropdownOptionFontSize;
    }
    .search-no-options {
      .search-no-options-wrapper {
        font-size: .875rem;
        line-height: 1.25rem;
      }
    }
  }
  &:not(.comfort-design) {
    $dropdownOptionFontSize: .75rem;
    .vs__dropdown-option {
      font-size: $dropdownOptionFontSize;
    }
    .search-no-options {
      .search-no-options-wrapper {
        font-size: .75rem;
        line-height: 1rem;
      }
    }
  }
  box-shadow: var(--fts-elevation-3);
  border: 0;
  border-radius: .25rem;
  background: var(--fts-popover-background);
  color: var(--fts-text);
  z-index: 1070 !important;
  .vs__dropdown-option {
    color: var(--fts-text);
    &:hover {
      background: var(--fts-gray-2);
      color: var(--fts-text);
    }
  }
  .vs__dropdown-option--highlight {
    background: var(--fts-gray-2);
    color: var(--fts-text);
  }
  .vs__dropdown-option--disabled {
    color: var(--fts-disabled) !important;
    background: transparent !important;
    filter: grayscale(1);
    cursor: not-allowed;
  }
  .search-no-options {
    @include flex-center-middle;
    flex-direction: column;
    padding: .575rem 1rem;
    color: var(--fts-gray-5);
    .search-no-options-wrapper {
      display: flex;
      align-items: center;
    }
    .search-no-options-icon {
      margin-right: .575rem;
    }
  }
}

</style>